import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Add as AddIcon,
  ArrowBack,
  Schedule as ScheduleIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useSubscription } from '../contexts/SubscriptionContext';

interface NavigationBarProps {
  onSignOut: () => void;
  onMenuToggle?: () => void;
  onNewChat?: () => void;
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  onSignOut,
  onMenuToggle,
  onNewChat,
}) => {
  const navigate = useNavigate();
  const { insuranceReportId } = useWebSocket();
  const { subscription, openBillingPortal } = useSubscription();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Payment failed but Stripe is still retrying (grace period): keep access and
  // prompt the user to update their card via the billing portal.
  const paymentPastDue = subscription?.status === 'past_due';
  const handleUpdatePayment = async () => {
    try {
      await openBillingPortal();
    } catch {
      // Swallow; the portal is also reachable from the billing settings tab.
    }
  };

  // Trial countdown shown next to Sign Out. This status cluster is also where
  // usage-limit indicators will live once those are implemented.
  const trialDaysLeft =
    subscription?.subscription_type === 'trial' && subscription.valid_until
      ? Math.max(
          0,
          Math.ceil((new Date(subscription.valid_until).getTime() - Date.now()) / 86_400_000)
        )
      : null;
  const trialUrgent = trialDaysLeft !== null && trialDaysLeft <= 3;
  const trialLabel =
    trialDaysLeft === null
      ? ''
      : trialDaysLeft === 0
        ? 'Trial · ends today'
        : trialDaysLeft === 1
          ? 'Trial · 1 day left'
          : `Trial · ${trialDaysLeft} days left`;

  const handleBackToSummary = () => {
    if (insuranceReportId) {
      navigate(`/insurance-summaries/${insuranceReportId}`);
    }
  };

  return (
    <AppBar 
      position="fixed" 
      color="primary"
      elevation={2}
      sx={{ 
        zIndex: (theme) => theme.zIndex.appBar + 1,
        borderRadius: 0, // Remove rounded corners
        width: '100%',
        top: 0,
        left: 0,
      }}
    >
      <Toolbar>
        {/* Mobile Menu Toggle */}
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={onMenuToggle}
            sx={{
              mr: 2,
              color: 'rgba(0, 0, 0, 0.87)',
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Back to Summary Button */}
        {insuranceReportId && (
          <IconButton
            color="inherit"
            onClick={handleBackToSummary}
            sx={{
              mr: 2,
              color: 'rgba(0, 0, 0, 0.87)',
            }}
            title="Back to Insurance Summary"
            aria-label="Back to Insurance Summary"
          >
            <ArrowBack />
            {!isMobile && (
              <Typography sx={{ ml: 0.5, fontSize: '0.875rem' }}>
                Back to Summary
              </Typography>
            )}
          </IconButton>
        )}

        {/* Brand Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={new URL('../assets/gekkovet-logo.png', import.meta.url).href}
            alt="Gekkovet Logo"
            sx={{ 
              mr: 1.5, 
              height: 40,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              color: 'rgba(0, 0, 0, 0.87)',
              fontWeight: 300, // Much thinner font
              fontSize: '1.1rem', // Smaller size
              letterSpacing: '0.5px', // Slight letter spacing for elegance
            }}
          >
          </Typography>
        </Box>
        
        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Payment failed — clickable chip opens the Stripe portal to update card */}
        {paymentPastDue && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: isMobile ? 1 : 2 }}>
            <Chip
              size="small"
              clickable
              onClick={handleUpdatePayment}
              icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
              label={isMobile ? 'Update card' : 'Payment failed · Update card'}
              aria-label="Payment failed. Update your card."
              sx={{
                fontWeight: 600,
                bgcolor: 'rgba(211, 47, 47, 0.12)',
                color: '#b71c1c',
                border: '1px solid',
                borderColor: 'rgba(211, 47, 47, 0.4)',
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Box>
        )}

        {/* Status indicators (trial countdown now; usage limits later) */}
        {trialDaysLeft !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: isMobile ? 1 : 2 }}>
            <Chip
              size="small"
              icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
              label={isMobile ? `Trial ${trialDaysLeft}d` : trialLabel}
              aria-label={trialLabel || `Trial ${trialDaysLeft} days left`}
              sx={{
                fontWeight: 600,
                bgcolor: trialUrgent ? 'rgba(211, 47, 47, 0.12)' : 'rgba(0, 0, 0, 0.06)',
                color: trialUrgent ? '#b71c1c' : 'rgba(0, 0, 0, 0.75)',
                border: '1px solid',
                borderColor: trialUrgent ? 'rgba(211, 47, 47, 0.4)' : 'rgba(0, 0, 0, 0.15)',
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Box>
        )}

        {/* Desktop Logout Button */}
        {!isMobile && (
          <Button
            variant="outlined"
            onClick={onSignOut}
            sx={{
              mr: 2,
              borderColor: 'rgba(0, 0, 0, 0.23)',
              color: 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Sign Out
          </Button>
        )}

        {/* Mobile New Chat Button */}
        {isMobile && onNewChat && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onNewChat}
            sx={{
              mr: 1,
              borderColor: 'rgba(0, 0, 0, 0.23)',
              color: 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            New
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default NavigationBar;
